/**
 * Filter plugin registry.
 *
 * Stores a flat array of FilterPlugin<unknown> instances with type erasure (DD-5).
 * Validates key uniqueness at initialization time (runtime assertion).
 * Provides helpers to filter and sort plugins by placement.
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 3.2)
 */

import { FilterPlugin } from "./types";

// Main plugins (order 1-7)
import { programFilter } from "./plugins/ProgramFilter";
import { competitionFilter } from "./plugins/CompetitionFilter";
import { statusFilter } from "./plugins/StatusFilter";
import { regionFilter } from "./plugins/RegionFilter";
import { districtFilter } from "./plugins/DistrictFilter";
import { labelFilter } from "./plugins/LabelFilter";
import { searchFilter } from "./plugins/SearchFilter";

// Advanced plugins (order 1-6)
import { OperatorFilterPlugin } from "./plugins/OperatorFilter";
import { ProjectFilterPlugin } from "./plugins/ProjectFilter";
import { TargetGroupFilterPlugin } from "./plugins/TargetGroupFilter";
import { DateRangeFilterPlugin } from "./plugins/DateRangeFilter";
import { AllocationFilterPlugin } from "./plugins/AllocationFilter";
import { BarometerFilterPlugin } from "./plugins/BarometerFilter";

// Hidden plugins (headless — participate in serialization only, no UI component)
import { schemeFilter } from "./plugins/SchemeFilter";

/**
 * Validates and freezes the plugin registry.
 *
 * @param plugins - Array of plugins to register
 * @returns Frozen array of validated plugins
 * @throws Error if any plugin keys are duplicated
 */
export function createFilterRegistry(
  plugins: FilterPlugin<unknown>[]
): ReadonlyArray<FilterPlugin<unknown>> {
  // Runtime assertion: validate key uniqueness
  const seen = new Set<string>();
  for (const plugin of plugins) {
    if (seen.has(plugin.key)) {
      throw new Error(
        `Duplicate filter plugin key: "${plugin.key}". Each plugin must have a unique key.`
      );
    }
    seen.add(plugin.key);
  }

  return Object.freeze([...plugins]);
}

/**
 * Returns main-placement plugins sorted by order.
 */
export function getMainFilters(
  registry: ReadonlyArray<FilterPlugin<unknown>>
): FilterPlugin<unknown>[] {
  return registry
    .filter((p) => p.placement === "main")
    .sort((a, b) => a.order - b.order);
}

/**
 * Returns advanced-placement plugins sorted by order.
 */
export function getAdvancedFilters(
  registry: ReadonlyArray<FilterPlugin<unknown>>
): FilterPlugin<unknown>[] {
  return registry
    .filter((p) => p.placement === "advanced")
    .sort((a, b) => a.order - b.order);
}

/**
 * The application's filter registry with all 14 plugins.
 *
 * Main (7): Program, Competition, Status, Region, District, Label, Search
 * Advanced (6): Operator, Project, TargetGroup, DateRange, Allocation, Barometer
 * Hidden (1): Scheme (headless — serialization only, UI via SavedViewsBar preset pills)
 */
export const filterRegistry = createFilterRegistry([
  // Main plugins (order 1-7)
  programFilter as FilterPlugin<unknown>,
  competitionFilter as FilterPlugin<unknown>,
  statusFilter as FilterPlugin<unknown>,
  regionFilter as FilterPlugin<unknown>,
  districtFilter as FilterPlugin<unknown>,
  labelFilter as FilterPlugin<unknown>,
  searchFilter as FilterPlugin<unknown>,
  // Advanced plugins (order 1-6)
  OperatorFilterPlugin as FilterPlugin<unknown>,
  ProjectFilterPlugin as FilterPlugin<unknown>,
  TargetGroupFilterPlugin as FilterPlugin<unknown>,
  DateRangeFilterPlugin as FilterPlugin<unknown>,
  AllocationFilterPlugin as FilterPlugin<unknown>,
  BarometerFilterPlugin as FilterPlugin<unknown>,
  // Hidden plugins (headless, serialization only)
  schemeFilter as FilterPlugin<unknown>,
]);
